import { RaisedButton } from '@eduact/ed-components'
import { FlexLayout, Typography } from '@eduact/ed-system'
import { coursePlayerPath, courseParentPath, useCourseParentType } from '@Utilities/CoursePaths/coursePaths'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

type BlockingUnit = { id: number; name: string; type: string }

export const getSequentialLockPayload = (error: any): BlockingUnit | null => {
	if (error?.status === 403 && error?.data?.data?.code === 'SEQUENTIAL_UNIT_LOCKED') {
		return error.data.data.blocking_unit ?? null
	}
	return null
}

type Props = {
	error: unknown
	onRetry?: () => void
}

/**
 * Shared error state for every unit viewer. Understands the backend unit
 * access contract: a 403 SEQUENTIAL_UNIT_LOCKED redirects to the blocking
 * unit (the snackbar already fired via the show flag), a 404 renders a
 * "content no longer available" state with a way back, anything else gets a
 * localized generic error with a retry action.
 */
const UnitErrorState: React.FC<Props> = ({ error, onRetry }) => {
	const [t] = useTranslation('course')
	const navigate = useNavigate()
	const { label, cId } = useParams()
	const parentType = useCourseParentType()

	const blockingUnit = getSequentialLockPayload(error)
	const isNotFound = (error as any)?.status === 404

	useEffect(() => {
		if (blockingUnit && blockingUnit.type && label && cId) {
			const path = coursePlayerPath(parentType, label, Number(cId))
			navigate({ pathname: `${path}/${blockingUnit.type}/${blockingUnit.id}` }, { replace: true })
		}
	}, [blockingUnit?.id])

	const message = blockingUnit
		? t('complete_first', { name: blockingUnit.name })
		: isNotFound
		? t('content_unavailable')
		: t('generic_error')

	return (
		<FlexLayout flexDirection="column" alignItems="center" justifyContent="center" width="100%" height="100%" gridGap="1rem" p="2rem">
			<Typography fontWeight="600" fontSize={{ sm: '1rem', lg: '1.25rem' }} textAlign="center">
				{message}
			</Typography>
			{isNotFound && label && (
				<RaisedButton btnSize="small" onClick={() => navigate(courseParentPath(parentType, label))}>
					{t('back_to_course')}
				</RaisedButton>
			)}
			{!isNotFound && !blockingUnit && onRetry && (
				<RaisedButton btnSize="small" onClick={onRetry}>
					{t('retry')}
				</RaisedButton>
			)}
		</FlexLayout>
	)
}

export default UnitErrorState
